import { Children, forwardRef, type ComponentPropsWithRef } from "react";

import { Circle, cn } from "@eisox/design-system";
import { ChevronDownIcon, TickIcon, WarningIcon } from "@eisox/icons";
import * as Collapsible from "@radix-ui/react-collapsible";
import { Slot } from "@radix-ui/react-slot";

import { m } from "~/paraglide/messages";

/* -------------------------------------------------------------------------------------------------
 * ErrorCollapsible
 * -----------------------------------------------------------------------------------------------*/

interface ErrorCollapsibleProps {
  className?: string;
  disabled?: boolean;
  children?: React.ReactNode;
}

const Root: React.FC<ErrorCollapsibleProps> = ({ className, disabled, children, ...props }) => {
  const errors = Children.count(children);

  return (
    <Collapsible.Root
      {...props}
      className={cn("rounded-l-[10px] bg-primary-green", errors > 0 && "bg-primary-red", className)}
      disabled={disabled}
    >
      <Collapsible.Trigger
        {...props}
        className={cn(
          "font-semibold box-border flex h-[60px] w-full items-center gap-[10px] px-[20px] py-[10px] text-primary-white [&_.chevron]:ml-auto [&_.icon]:h-[20px] [&_.icon]:w-[20px]",
          className,
        )}
      >
        <Circle.Root color={errors > 0 ? "red" : "green"} backgroundColor="white">
          {errors > 0 ? <WarningIcon className="h-[20px] w-[20px]" /> : <TickIcon className="h-[20px] w-[20px]" />}
        </Circle.Root>
        {m.errors_count({ count: errors })}
        {errors > 0 && <ChevronDownIcon className="ml-auto" />}
      </Collapsible.Trigger>
      <Collapsible.Content {...props} asChild>
        {/* Radix Collapsible animation problem : force to add asChild and className to children div to fix animation problem */}
        <div
          className={cn(
            "error-collapsible-content py-0 flex max-h-[126px] flex-col gap-[10px] overflow-x-hidden overflow-y-auto px-[20px] text-base",
            className,
          )}
        >
          {children}
        </div>
      </Collapsible.Content>
    </Collapsible.Root>
  );
};

Root.displayName = "ErrorCollapsible";

/* -------------------------------------------------------------------------------------------------
 * Error
 * -----------------------------------------------------------------------------------------------*/

interface ErrorProps extends ComponentPropsWithRef<"div"> {
  asChild?: boolean;
}

const ErrorItem = forwardRef<HTMLDivElement, ErrorProps>(({ asChild, className, children, ...props }, forwardedRef) => {
  const Comp = asChild ? Slot : "div";
  return (
    <Comp
      {...props}
      ref={forwardedRef}
      className={cn("shrink-0 cursor-pointer text-primary-white last:mb-[10px]", className)}
    >
      {children}
    </Comp>
  );
});

ErrorItem.displayName = "Error";

export { ErrorItem, Root };
export type { ErrorCollapsibleProps, ErrorProps };
